import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Send } from 'lucide-react';
import {
  labOrderService,
  labOrderItemService,
  labResultService,
  labTestService,
  LabOrder,
  LabOrderItem,
  LabResult
} from '../../lib/services.lab';
import { useStore } from '../../lib/store';
import { getUserRoleFromDB } from '../../lib/rbac';
import toast from 'react-hot-toast';

interface ResultEntry {
  id?: string;
  order_item_id: string;
  analyte: string;
  value: string;
  unit: string;
  ref_low: number;
  ref_high: number;
  flag?: string;
}

export default function ResultEntry() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { user } = useStore();
  const [order, setOrder] = useState<LabOrder | null>(null);
  const [orderItems, setOrderItems] = useState<LabOrderItem[]>([]);
  const [results, setResults] = useState<Record<string, ResultEntry[]>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [canEnterResults, setCanEnterResults] = useState(false);

  useEffect(() => {
    checkPermissions();
    if (orderId) {
      loadOrder();
    }
  }, [orderId]);

  const checkPermissions = async () => {
    if (!user?.id) return;
    try {
      const role = await getUserRoleFromDB(user.id);
      setCanEnterResults(role === 'admin' || role === 'lab_tech' || role === 'pathologist');
    } catch (error) {
      console.error('Error checking permissions:', error);
    }
  };

  const loadOrder = async () => {
    if (!orderId) return;

    try {
      const orderData = await labOrderService.getById(orderId);
      if (!orderData) {
        toast.error('Order not found');
        navigate('/lab');
        return;
      }

      setOrder(orderData);

      const items = await labOrderItemService.getByOrder(orderId);
      setOrderItems(items);

      const resultsData: Record<string, ResultEntry[]> = {};
      
      for (const item of items) {
        const existingResults = await labResultService.getByOrderItem(item.id);
        
        if (existingResults.length > 0) {
          resultsData[item.id] = existingResults.map(r => ({
            id: r.id,
            order_item_id: r.order_item_id,
            analyte: r.analyte,
            value: r.value || '',
            unit: r.unit || '',
            ref_low: r.ref_low || 0,
            ref_high: r.ref_high || 0,
            flag: r.flag
          }));
        } else {
          resultsData[item.id] = await generateDefaultResults(item);
        }
      }

      setResults(resultsData);
    } catch (error) {
      console.error('Failed to load order:', error);
      toast.error('Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  const generateDefaultResults = async (item: LabOrderItem): Promise<ResultEntry[]> => {
    const test = item.test;
    if (!test) return [];

    const defaultResults: Record<string, ResultEntry[]> = {
      'HB': [{
        order_item_id: item.id,
        analyte: 'Hemoglobin',
        value: '',
        unit: 'g/dL',
        ref_low: 12,
        ref_high: 16
      }],
      'WBC': [{
        order_item_id: item.id,
        analyte: 'WBC Count',
        value: '',
        unit: '×10³/μL',
        ref_low: 4,
        ref_high: 11
      }],
      'PLATELET': [{
        order_item_id: item.id,
        analyte: 'Platelet Count',
        value: '',
        unit: '×10³/μL',
        ref_low: 150,
        ref_high: 400
      }],
      'ALT': [{
        order_item_id: item.id,
        analyte: 'ALT (SGPT)',
        value: '',
        unit: 'U/L',
        ref_low: 7,
        ref_high: 56
      }],
      'AST': [{
        order_item_id: item.id,
        analyte: 'AST (SGOT)',
        value: '',
        unit: 'U/L',
        ref_low: 10,
        ref_high: 40
      }],
      'ALP': [{
        order_item_id: item.id,
        analyte: 'Alkaline Phosphatase',
        value: '',
        unit: 'U/L',
        ref_low: 44,
        ref_high: 147
      }],
      'BILIRUBIN': [{
        order_item_id: item.id,
        analyte: 'Total Bilirubin',
        value: '',
        unit: 'mg/dL',
        ref_low: 0.3,
        ref_high: 1.2
      }],
      'CREATININE': [{
        order_item_id: item.id,
        analyte: 'Serum Creatinine',
        value: '',
        unit: 'mg/dL',
        ref_low: 0.6,
        ref_high: 1.2
      }],
      'FBS': [{
        order_item_id: item.id,
        analyte: 'Fasting Blood Sugar',
        value: '',
        unit: 'mg/dL',
        ref_low: 70,
        ref_high: 100
      }],
      'PPBS': [{
        order_item_id: item.id,
        analyte: 'Post Prandial Blood Sugar',
        value: '',
        unit: 'mg/dL',
        ref_low: 70,
        ref_high: 140
      }]
    };

    return defaultResults[test.code] || [{
      order_item_id: item.id,
      analyte: test.name,
      value: '',
      unit: '',
      ref_low: 0,
      ref_high: 0
    }];
  };

  const calculateFlag = (value: string, refLow: number, refHigh: number): string => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '';
    if (numValue < refLow) return 'L';
    if (numValue > refHigh) return 'H';
    return 'N';
  };

  const handleResultChange = (itemId: string, index: number, field: keyof ResultEntry, value: string | number) => {
    setResults(prev => {
      const itemResults = [...prev[itemId]];
      const result = { ...itemResults[index] };
      
      if (field === 'value') {
        result.value = value as string;
        result.flag = calculateFlag(value as string, result.ref_low, result.ref_high);
      } else {
        (result as any)[field] = value;
      }
      
      itemResults[index] = result;
      return { ...prev, [itemId]: itemResults };
    });
  };

  const handleSaveDraft = async () => {
    if (!user?.id) return;
    setSaving(true);

    try {
      for (const itemId in results) {
        for (const result of results[itemId]) {
          if (!result.value) continue;

          if (result.id) {
            await labResultService.update(result.id, {
              value: result.value,
              flag: result.flag,
              entered_by: user.id
            });
          } else {
            await labResultService.create({
              order_item_id: result.order_item_id,
              analyte: result.analyte,
              value: result.value,
              unit: result.unit,
              ref_low: result.ref_low,
              ref_high: result.ref_high,
              flag: result.flag,
              entered_by: user.id
            });
          }
        }
      }

      toast.success('Results saved as draft');
      await loadOrder();
    } catch (error) {
      console.error('Failed to save results:', error);
      toast.error('Failed to save results');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitForVerification = async () => {
    if (!user?.id) return;

    const hasAllResults = orderItems.every(item => {
      const itemResults = results[item.id] || [];
      return itemResults.every(r => r.value);
    });

    if (!hasAllResults) {
      toast.error('Please enter all result values before submitting');
      return;
    }

    setSaving(true);

    try {
      for (const itemId in results) {
        for (const result of results[itemId]) {
          if (result.id) {
            await labResultService.update(result.id, {
              value: result.value,
              flag: result.flag,
              entered_by: user.id
            });
          } else {
            await labResultService.create({
              order_item_id: result.order_item_id,
              analyte: result.analyte,
              value: result.value,
              unit: result.unit,
              ref_low: result.ref_low,
              ref_high: result.ref_high,
              flag: result.flag,
              entered_by: user.id
            });
          }
        }

        await labOrderItemService.updateStatus(itemId, 'in_progress');
      }

      if (orderId) {
        await labOrderService.updateStatus(orderId, 'in_progress');
      }

      toast.success('Results submitted for verification');
      navigate('/lab');
    } catch (error) {
      console.error('Failed to submit results:', error);
      toast.error('Failed to submit results');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!canEnterResults) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">You don't have permission to enter results.</p>
      </div>
    );
  }

  return (
    <section className="space-y-6">
      <header className="flex items-center gap-4">
        <button
          onClick={() => navigate('/lab')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Result Entry</h1>
          <p className="text-sm text-neutral-600">
            Order #{orderId?.substring(0, 8)} - {order?.patient?.name}
          </p>
        </div>
      </header>

      {/* Order Items with Results */}
      <div className="space-y-6">
        {orderItems.map((item) => (
          <div key={item.id} className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-4">
              {item.test?.name} ({item.test?.code})
            </h3>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Analyte</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unit</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference Range</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Flag</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {(results[item.id] || []).map((result, index) => (
                    <tr key={index}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {result.analyte}
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          value={result.value}
                          onChange={(e) => handleResultChange(item.id, index, 'value', e.target.value)}
                          className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter value"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {result.unit}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {result.ref_low} - {result.ref_high}
                      </td>
                      <td className="px-4 py-3">
                        {result.flag && (
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            result.flag === 'L' ? 'bg-blue-100 text-blue-800' :
                            result.flag === 'H' ? 'bg-red-100 text-red-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {result.flag === 'L' ? 'Low' : result.flag === 'H' ? 'High' : 'Normal'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex gap-3 justify-end">
        <button
          onClick={() => navigate('/lab')}
          className="px-6 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={handleSaveDraft}
          disabled={saving}
          className="inline-flex items-center px-6 py-2 border border-blue-600 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-50 disabled:opacity-50"
        >
          <Save className="w-4 h-4 mr-2" />
          Save Draft
        </button>
        <button
          onClick={handleSubmitForVerification}
          disabled={saving}
          className="inline-flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          <Send className="w-4 h-4 mr-2" />
          Submit for Verification
        </button>
      </div>
    </section>
  );
}
